import React, { useCallback } from 'react';
import Victory from '@/victory';
import { GraphItemData } from './types';
import { useTheme } from '@/contexts/theme-context';

interface chartPadding {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

interface TotalSalesGraphProps {
  barWidth?: number;
  chartHeight?: number;
  chartPadding: chartPadding;
  currencySymbol?: string;
  salesData: GraphItemData[];
  showXAxisLabels?: boolean;
  showYAxisLabels?: boolean;
  timePeriod: number;
}
const MAX_DATA_ITEMS_FOR_GRAPH_BAR_WIDTH_24 = 20;
const MAX_DATA_ITEMS_FOR_GRAPH_BAR_WIDTH_8 = 40;

export const BarGraph: React.FC<TotalSalesGraphProps> = ({
  barWidth,
  chartHeight = 200,
  chartPadding,
  currencySymbol = '',
  showXAxisLabels = true,
  showYAxisLabels = true,
  salesData,
  timePeriod,
}) => {
  const theme = useTheme();

  const axisLabelStyle = {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primary,
    fontSize: 11,
    lineHeight: 12,
  };
  const chartStyles = {
    barStyle: {
      fill: theme.colors.accent,
    },
    tooltipStyle: {
      fill: theme.colors.white,
      fontFamily: theme.fonts.primary,
      stroke: theme.colors.primary,
      strokeWidth: 0,
    },
    xAxisStyle: {
      tickLabels: {
        ...axisLabelStyle,
      },
    },
    yAxisStyle: {
      axis: { stroke: theme.colors.transparent },
      tickLabels: {
        ...axisLabelStyle,
      },
    },
  };

  const formatTick = useCallback(
    (tick: number | string) => {
      if (typeof tick !== 'number') {
        // If tick is not a number, return it without formatting
        return tick;
      }

      if (tick >= 1000000) {
        return `${currencySymbol}${Math.round(tick / 1000000)}M`;
      } else if (tick >= 1000) {
        return `${currencySymbol}${Math.round(tick / 1000)}K`;
      } else {
        return `${currencySymbol}${Math.round(tick)}`;
      }
    },
    [currencySymbol],
  );

  const calculateBarWidth = (dataLength: number) => {
    // The data length and width is hardcoded based on local testing results.
    // As we go above 60 data points the bars merge together and become unreadable.
    // reducing the width below 0.5 doesn't help.
    if (dataLength < MAX_DATA_ITEMS_FOR_GRAPH_BAR_WIDTH_24) {
      return 24;
    } else if (
      dataLength >= MAX_DATA_ITEMS_FOR_GRAPH_BAR_WIDTH_24 &&
      dataLength <= MAX_DATA_ITEMS_FOR_GRAPH_BAR_WIDTH_8
    ) {
      return 8;
    } else {
      return 4;
    }
  };

  const calculateXYDomain = (): {
    x: [number, number];
    y?: [number, number];
  } => {
    const minDomainX = 0.5; // Default minimum x domain
    const maxDomainX = minDomainX + timePeriod;

    if (!showYAxisLabels) {
      return {
        x: [minDomainX, maxDomainX],
      };
    }

    const minDomainY = areAllYValuesZero(salesData) ? 0.1 : 0;
    const maxDomainY = Math.max(...salesData.map((item) => item.y));
    // Calculate the nearest rounded value of maxDomainY
    let domainYFactor = 0;
    if (maxDomainY > 0) {
      const nearestRoundedValue = Math.pow(
        10,
        Math.floor(Math.log10(maxDomainY)),
      );
      const roundedMaxDomainY =
        Math.ceil(maxDomainY / nearestRoundedValue) * nearestRoundedValue;

      // Calculate the factor for the y domain
      domainYFactor = roundedMaxDomainY / 10;
    }

    return {
      x: [minDomainX, maxDomainX],
      y: [minDomainY, maxDomainY + domainYFactor],
    };
  };

  return (
    <Victory.VictoryChart
      domain={calculateXYDomain()}
      height={chartHeight}
      padding={chartPadding}
    >
      <Victory.VictoryAxis
        crossAxis={false}
        dependentAxis
        fixLabelOverlap
        style={chartStyles.yAxisStyle}
        tickCount={areAllYValuesZero(salesData) ? 1 : 3}
        tickFormat={showYAxisLabels ? formatTick : () => ''}
        tickLabelComponent={<Victory.VictoryLabel textAnchor="start" x={0} />}
      />
      <Victory.VictoryBar
        animate={
          !areAllYValuesZero(salesData)
            ? { duration: 2000, onLoad: { duration: 1000 } }
            : undefined
        }
        barWidth={barWidth ? barWidth : calculateBarWidth(salesData.length)}
        data={salesData}
        labelComponent={
          <Victory.VictoryTooltip
            constrainToVisibleArea
            flyoutStyle={chartStyles.tooltipStyle}
            pointerLength={0}
            renderInPortal={false}
          />
        }
        padding={{ top: 0 }}
        style={{
          data: chartStyles.barStyle,
        }}
      />
      <Victory.VictoryAxis
        crossAxis
        fixLabelOverlap
        style={chartStyles.xAxisStyle}
        tickCount={4}
        tickFormat={showXAxisLabels ? formatTick : () => ''}
      />
    </Victory.VictoryChart>
  );
};

function areAllYValuesZero(data: GraphItemData[]): boolean {
  return data.every((item) => item.y === 0);
}
